import React, { useState } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Table,
  Badge,
  Card,
  Modal,
  Textarea,
  Alert,
  Loader,
  Divider,
  Grid,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconDownload,
  IconUser,
  IconCalendar,
  IconFileText,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useEvaluationReport,
  useCheckReport,
  useRejectCheck,
  useApproveReport,
  useRejectApproval,
  useApproveByBidCommittee,
  useRejectByBidCommittee,
  useAddBidCommitteeMembers,
} from '../hooks/useEvaluationReports';
import { useAuth } from '../../../auth/AuthContext';
import { formatCurrency } from '../../../utils/currency';
import { getCurrencySymbol } from '../../../utils/currency';
import dayjs from 'dayjs';
import { AddBidCommitteeMembersModal } from '../components/AddBidCommitteeMembersModal';

export function EvaluationReportDetail() {
  const { id: tenderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  console.log('EvaluationReportDetail - tenderId from params:', tenderId);
  
  const queryResult = useEvaluationReport(tenderId || '');
  const { data: report, isLoading, error, isError, isFetching, dataUpdatedAt } = queryResult;
  
  console.log('EvaluationReportDetail - full query result:', queryResult);
  console.log('EvaluationReportDetail - report:', report);
  console.log('EvaluationReportDetail - isLoading:', isLoading);
  console.log('EvaluationReportDetail - isFetching:', isFetching);
  console.log('EvaluationReportDetail - error:', error);
  console.log('EvaluationReportDetail - isError:', isError);
  console.log('EvaluationReportDetail - dataUpdatedAt:', dataUpdatedAt);
  
  const [checkModalOpened, setCheckModalOpened] = useState(false);
  const [approveModalOpened, setApproveModalOpened] = useState(false);
  const [rejectModalOpened, setRejectModalOpened] = useState(false);
  const [comments, setComments] = useState('');
  const [actionType, setActionType] = useState<'check' | 'approve' | 'reject' | 'bid-committee'>('check');
  const [bidCommitteeModalOpened, setBidCommitteeModalOpened] = useState(false);

  const checkReport = useCheckReport();
  const rejectCheck = useRejectCheck();
  const approveReport = useApproveReport();
  const rejectApproval = useRejectApproval();
  const approveByBidCommittee = useApproveByBidCommittee();
  const rejectByBidCommittee = useRejectByBidCommittee();
  const addBidCommitteeMembers = useAddBidCommitteeMembers();

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Loader size="lg" />
      </Container>
    );
  }

  if (error) {
    console.error('Error loading evaluation report:', error);
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Error">
          Failed to load evaluation report: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" title="No Report Found">
          No evaluation report found for this tender.
        </Alert>
      </Container>
    );
  }

  if (!report.reportData) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Invalid Report">
          The evaluation report data is missing or invalid.
        </Alert>
      </Container>
    );
  }

  const reportData = report.reportData;
  const currency = reportData.tender?.currency || 'MVR';

  // Determine user's role and permissions
  const canCheck = user?.role === 'PROCUREMENT_OFFICER' && 
    report.status === 'PENDING_CHECK' && 
    report.checkedById === user.id;
  
  const canApprove = user?.role === 'HEAD_OF_PROCUREMENT' && 
    report.status === 'PENDING_APPROVAL' && 
    report.approvedById === user.id;
  
  const canAddBidCommittee = (user?.role === 'PROCUREMENT_OFFICER' || user?.role === 'HEAD_OF_PROCUREMENT' || user?.role === 'ADMIN') &&
    report.status === 'PENDING_BID_COMMITTEE';
  
  const userBidCommitteeApproval = report.bidCommitteeApprovals?.find(
    (a) => a.memberId === user?.id && a.status === 'PENDING'
  );
  const canApproveBidCommittee = user?.role === 'BID_COMMITTEE_MEMBER' && !!userBidCommitteeApproval;

  const handleCheck = () => {
    setActionType('check');
    setCheckModalOpened(true);
  };

  const handleApprove = () => {
    setActionType('approve');
    setApproveModalOpened(true);
  };

  const handleReject = () => {
    setActionType('reject');
    setRejectModalOpened(true);
  };

  const handleBidCommitteeApprove = () => {
    setActionType('bid-committee');
    setApproveModalOpened(true);
  };

  const handleBidCommitteeReject = () => {
    setActionType('bid-committee');
    setRejectModalOpened(true);
  };

  const submitCheck = () => {
    checkReport.mutate(
      { reportId: report.id, data: { comments: comments.trim() || undefined } },
      {
        onSuccess: () => {
          setCheckModalOpened(false);
          setComments('');
        },
      }
    );
  };

  const submitRejectCheck = () => {
    rejectCheck.mutate(
      { reportId: report.id, data: { comments: comments.trim() } },
      {
        onSuccess: () => {
          setRejectModalOpened(false);
          setComments('');
        },
      }
    );
  };

  const submitApprove = () => {
    approveReport.mutate(
      { reportId: report.id, data: { comments: comments.trim() || undefined } },
      {
        onSuccess: () => {
          setApproveModalOpened(false);
          setComments('');
        },
      }
    );
  };

  const submitRejectApproval = () => {
    rejectApproval.mutate(
      { reportId: report.id, data: { comments: comments.trim() } },
      {
        onSuccess: () => {
          setRejectModalOpened(false);
          setComments('');
        },
      }
    );
  };

  const submitBidCommitteeApprove = () => {
    approveByBidCommittee.mutate(
      { reportId: report.id, data: { comments: comments.trim() || undefined } },
      {
        onSuccess: () => {
          setApproveModalOpened(false);
          setComments('');
        },
      }
    );
  };

  const submitBidCommitteeReject = () => {
    rejectByBidCommittee.mutate(
      { reportId: report.id, data: { comments: comments.trim() } },
      {
        onSuccess: () => {
          setRejectModalOpened(false);
          setComments('');
        },
      }
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING_CHECK':
        return <Badge color="yellow">Pending Check</Badge>;
      case 'PENDING_APPROVAL':
        return <Badge color="orange">Pending Approval</Badge>;
      case 'PENDING_BID_COMMITTEE':
        return <Badge color="blue">Pending Bid Committee</Badge>;
      case 'APPROVED':
        return <Badge color="green">Approved</Badge>;
      case 'REJECTED':
        return <Badge color="red">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // TODO: Implement PDF download
      // const blob = await evaluationReportsApi.downloadReportPDF(tenderId!);
      // const url = window.URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = `evaluation-report-${reportData.tender.referenceNo}.pdf`;
      // a.click();
      alert('PDF download will be implemented');
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/tenders/${tenderId}`)}
          >
            Back to Tender
          </Button>
        </Group>

        <Group justify="space-between" mb="xl">
          <div>
            <Title order={2} mb="xs">
              Tender Evaluation Report
            </Title>
            <Text c="dimmed">Tender Number: {reportData.tender.referenceNo}</Text>
          </div>
          <Group>
            {getStatusBadge(report.status)}
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadPDF}
              variant="light"
            >
              Download PDF
            </Button>
          </Group>
        </Group>

        {/* Tender Information */}
        <Card shadow="sm" p="lg" mb="md">
          <Title order={3} mb="md">
            Tender Information
          </Title>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Text size="sm" c="dimmed">
                Description
              </Text>
              <Text fw={500} mb="sm">
                {reportData.tender.description}
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Text size="sm" c="dimmed">
                Category
              </Text>
              <Text fw={500} mb="sm">
                {reportData.tender.category}
              </Text>
            </Grid.Col>
            {reportData.tender.submissionDeadline && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Submission Deadline
                </Text>
                <Text fw={500} mb="sm">
                  {dayjs(reportData.tender.submissionDeadline).format('DD MMM YYYY HH:mm')}
                </Text>
              </Grid.Col>
            )}
            {reportData.tender.openingDate && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Text size="sm" c="dimmed">
                  Opening Date
                </Text>
                <Text fw={500} mb="sm">
                  {dayjs(reportData.tender.openingDate).format('DD MMM YYYY HH:mm')}
                </Text>
              </Grid.Col>
            )}
          </Grid>
        </Card>

        {/* Evaluation Table */}
        <Card shadow="sm" p="lg" mb="md">
          <Title order={3} mb="md">
            Evaluation Results
          </Title>
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Company Name</Table.Th>
                  <Table.Th>Total Price ({getCurrencySymbol(currency)})</Table.Th>
                  {reportData.evaluationCriteria.map((criteria) => (
                    <Table.Th key={criteria.id}>
                      {criteria.criteriaName} ({criteria.weightage}%)
                    </Table.Th>
                  ))}
                  <Table.Th>Total Score (100%)</Table.Th>
                  <Table.Th>Notes</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {reportData.bidSubmissions.map((submission, index) => {
                  const totalScore = submission.scores.reduce((sum, score) => sum + score.score, 0);
                  return (
                    <Table.Tr key={submission.id}>
                      <Table.Td>{index + 1}</Table.Td>
                      <Table.Td>{submission.companyName}</Table.Td>
                      <Table.Td>{formatCurrency(submission.totalPrice, currency)}</Table.Td>
                      {reportData.evaluationCriteria.map((criteria) => {
                        const score = submission.scores.find((s) => s.criteriaId === criteria.id);
                        return (
                          <Table.Td key={criteria.id}>
                            {score ? score.score.toFixed(2) : '0.00'}
                          </Table.Td>
                        );
                      })}
                      <Table.Td fw={700}>{totalScore.toFixed(2)}</Table.Td>
                      <Table.Td>f
                        {submission.evaluationNotes ? (
                          <Text size="sm" lineClamp={2}>
                            {submission.evaluationNotes}
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            No notes
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        </Card>

        {/* Approval Status */}
        <Card shadow="sm" p="lg" mb="md">
          <Title order={3} mb="md">
            Approval Status
          </Title>
          <Stack gap="md">
            <div>
              <Text size="sm" c="dimmed" mb="xs">
                Prepared By
              </Text>
              <Text fw={500}>{report.generatedBy.fullName}</Text>
              <Text size="xs" c="dimmed">
                {dayjs(report.generatedAt).format('DD MMM YYYY')}
              </Text>
            </div>

            <Divider />

            <div>
              <Text size="sm" c="dimmed" mb="xs">
                Checked By
              </Text>
              {report.checkedBy ? (
                <>
                  <Text fw={500}>{report.checkedBy.fullName}</Text>
                  {report.checkedAt && (
                    <Text size="xs" c="dimmed">
                      {dayjs(report.checkedAt).format('DD MMM YYYY')}
                    </Text>
                  )}
                  {report.checkedComments && (
                    <Text size="sm" mt="xs">
                      {report.checkedComments}
                    </Text>
                  )}
                  <Badge color={report.checkedStatus === 'APPROVED' ? 'green' : report.checkedStatus === 'REJECTED' ? 'red' : 'yellow'} mt="xs">
                    {report.checkedStatus}
                  </Badge>
                </>
              ) : (
                <Text c="dimmed">Not assigned</Text>
              )}
            </div>

            <Divider />

            <div>
              <Text size="sm" c="dimmed" mb="xs">
                Approved By
              </Text>
              {report.approvedBy ? (
                <>
                  <Text fw={500}>{report.approvedBy.fullName}</Text>
                  {report.approvedAt && (
                    <Text size="xs" c="dimmed">
                      {dayjs(report.approvedAt).format('DD MMM YYYY')}
                    </Text>
                  )}
                  {report.approvalComments && (
                    <Text size="sm" mt="xs">
                      {report.approvalComments}
                    </Text>
                  )}
                  <Badge color={report.approvalStatus === 'APPROVED' ? 'green' : report.approvalStatus === 'REJECTED' ? 'red' : 'yellow'} mt="xs">
                    {report.approvalStatus}
                  </Badge>
                </>
              ) : (
                <Text c="dimmed">Not assigned</Text>
              )}
            </div>

            {report.bidCommitteeApprovals && report.bidCommitteeApprovals.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text size="sm" c="dimmed" mb="xs">
                    Bid Committee Members
                  </Text>
                  <Stack gap="xs">
                    {report.bidCommitteeApprovals.map((approval) => (
                      <div key={approval.id}>
                        <Group justify="space-between">
                          <Text fw={500}>{approval.member.fullName}</Text>
                          <Badge
                            color={
                              approval.status === 'APPROVED'
                                ? 'green'
                                : approval.status === 'REJECTED'
                                ? 'red'
                                : 'yellow'
                            }
                          >
                            {approval.status}
                          </Badge>
                        </Group>
                        {approval.approvedAt && (
                          <Text size="xs" c="dimmed">
                            {dayjs(approval.approvedAt).format('DD MMM YYYY')}
                          </Text>
                        )}
                        {approval.comments && (
                          <Text size="sm" mt="xs">
                            {approval.comments}
                          </Text>
                        )}
                      </div>
                    ))}
                  </Stack>
                </div>
              </>
            )}
          </Stack>
        </Card>

        {/* Action Buttons */}
        {(canCheck || canApprove || canApproveBidCommittee || canAddBidCommittee) && (
          <Card shadow="sm" p="lg">
            <Title order={3} mb="md">
              Actions
            </Title>
            <Group>
              {canCheck && (
                <>
                  <Button
                    leftSection={<IconCheck size={16} />}
                    color="green"
                    onClick={handleCheck}
                  >
                    Check Report
                  </Button>
                  <Button
                    leftSection={<IconX size={16} />}
                    color="red"
                    variant="light"
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                </>
              )}
              {canApprove && (
                <>
                  <Button
                    leftSection={<IconCheck size={16} />}
                    color="green"
                    onClick={handleApprove}
                  >
                    Approve Report
                  </Button>
                  <Button
                    leftSection={<IconX size={16} />}
                    color="red"
                    variant="light"
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                </>
              )}
              {canAddBidCommittee && (
                <Button
                  leftSection={<IconUser size={16} />}
                  onClick={() => setBidCommitteeModalOpened(true)}
                >
                  Add Bid Committee Members
                </Button>
              )}
              {canApproveBidCommittee && (
                <>
                  <Button
                    leftSection={<IconCheck size={16} />}
                    color="green"
                    onClick={handleBidCommitteeApprove}
                  >
                    Approve
                  </Button>
                  <Button
                    leftSection={<IconX size={16} />}
                    color="red"
                    variant="light"
                    onClick={handleBidCommitteeReject}
                  >
                    Reject
                  </Button>
                </>
              )}
            </Group>
          </Card>
        )}

        {/* Modals */}
        <Modal
          opened={checkModalOpened}
          onClose={() => {
            setCheckModalOpened(false);
            setComments('');
          }}
          title="Check Report"
        >
          <Stack>
            <Textarea
              label="Comments (Optional)"
              placeholder="Add any comments..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              minRows={3}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setCheckModalOpened(false)}>
                Cancel
              </Button>
              <Button onClick={submitCheck} loading={checkReport.isPending}>
                Approve Check
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={approveModalOpened}
          onClose={() => {
            setApproveModalOpened(false);
            setComments('');
          }}
          title={actionType === 'approve' ? 'Approve Report' : 'Approve (Bid Committee)'}
        >
          <Stack>
            <Textarea
              label="Comments (Optional)"
              placeholder="Add any comments..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              minRows={3}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setApproveModalOpened(false)}>
                Cancel
              </Button>
              <Button
                onClick={
                  actionType === 'approve' ? submitApprove : submitBidCommitteeApprove
                }
                loading={
                  actionType === 'approve'
                    ? approveReport.isPending
                    : approveByBidCommittee.isPending
                }
              >
                Approve
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={rejectModalOpened}
          onClose={() => {
            setRejectModalOpened(false);
            setComments('');
          }}
          title="Reject Report"
        >
          <Stack>
            <Alert color="red" mb="md">
              Please provide a reason for rejection. The report will be sent back to the generator.
            </Alert>
            <Textarea
              label="Rejection Comments (Required)"
              placeholder="Explain why you are rejecting this report..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              minRows={3}
              required
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setRejectModalOpened(false)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={
                  actionType === 'check'
                    ? submitRejectCheck
                    : actionType === 'approve'
                    ? submitRejectApproval
                    : submitBidCommitteeReject
                }
                loading={
                  actionType === 'check'
                    ? rejectCheck.isPending
                    : actionType === 'approve'
                    ? rejectApproval.isPending
                    : rejectByBidCommittee.isPending
                }
                disabled={!comments.trim()}
              >
                Reject
              </Button>
            </Group>
          </Stack>
        </Modal>

        <AddBidCommitteeMembersModal
          opened={bidCommitteeModalOpened}
          onClose={() => setBidCommitteeModalOpened(false)}
          reportId={report.id}
          onSuccess={() => {
            setBidCommitteeModalOpened(false);
          }}
        />
      </Paper>
    </Container>
  );
}

